"use client";

import AddRemoveImageButton from "@/components/AddRemoveImageButton";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { Flex, FlexColumn } from "@/components/Flex";
import HtmlText from "@/components/HtmlText";
import ResponsiveDialog from "@/features/ResponsiveDialog/ResponsiveDialog";
import DialogCloseBtn from "@/features/ResponsiveDialog/styled/DialogCloseBtn";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { ArrowLeft, ArrowRight } from "lucide-react";
import CircularProgress from "@/components/CircularProgress";
import StudentInformationCell from "../../../../features/UserContentManagement/ClassPage/TeacherView/components/StudentInformationCell";
import { getColor, getBackgroundColor, getProgressValue, getProgressBg } from "@/utils/progressPill";
import Circular from "@/components/Circular";
import { FlashcardsStudyAnalytics } from "@knowt/syncing/hooks/study/statsUtils";
import { formatTimeStudying } from "@knowt/syncing/utils/dateTimeUtils";
import { ClassMemberWithDetails, Flashcard, StudySessionProgressEnum } from "@knowt/syncing/graphql/schema";
import { capitalize } from "@knowt/syncing/utils/stringUtils";
import { useSyncedState } from "@knowt/syncing/utils/hooks/useSyncedState";
import StyledTable, { StyledTableColumn } from "@/components/styled/table/StyledTable";
import { SortOrder, SortSettings } from "@/components/styled/table/types";
import { useMemo, useState } from "react";
import br from "@/styles/breakpoints.module.css";
import clsx from "clsx";

enum SortKeys {
    Name = "name",
    Progress = "progress",
    TimeStudying = "timeStudying",
}

const MasteryFlashcardDetailsContent = ({
    currentFlashcardIndex,
    allFlashcards,
    members,
    flashcardsStudyAnalytics,
}: {
    currentFlashcardIndex: number;
    allFlashcards: Flashcard[];
    members: ClassMemberWithDetails[];
    flashcardsStudyAnalytics: FlashcardsStudyAnalytics | null | undefined;
}) => {
    const [flashcardIndex, setFlashcardIndex] = useSyncedState(currentFlashcardIndex);
    const [sortSettings, setSortSettings] = useState<SortSettings>({ sortBy: SortKeys.Name, order: SortOrder.ASC });

    const { term, definition, image, secondaryImage, flashcardId } = allFlashcards[flashcardIndex];
    const flashcardAnalytics = flashcardsStudyAnalytics?.[flashcardId];
    const flashcardsCount = allFlashcards.length;

    const membersById = members.reduce(
        (acc, member) => {
            acc[member.userId] = member;
            return acc;
        },
        {} as Record<string, ClassMemberWithDetails>
    );

    const sortedRows = useMemo(() => {
        const rows = Object.keys(flashcardAnalytics || {});

        switch (sortSettings.sortBy) {
            case SortKeys.Name:
                return rows.sort((a, b) => {
                    const nameA = membersById[a].Name || "";
                    const nameB = membersById[b].Name || "";

                    return sortSettings.order === SortOrder.ASC
                        ? nameA.localeCompare(nameB)
                        : nameB.localeCompare(nameA);
                });
            case SortKeys.Progress:
                return rows.sort((a, b) => {
                    const progressA = flashcardAnalytics?.[a].progress || StudySessionProgressEnum.NEW;
                    const progressB = flashcardAnalytics?.[b].progress || StudySessionProgressEnum.NEW;

                    if (progressA === progressB) return 0;

                    return sortSettings.order === SortOrder.ASC ? 1 : -1;
                });
            case SortKeys.TimeStudying:
                return rows.sort((a, b) => {
                    const timeStudyingA = flashcardAnalytics?.[a].timeSpent || 0;
                    const timeStudyingB = flashcardAnalytics?.[b].timeSpent || 0;

                    if (timeStudyingA === timeStudyingB) return 0;

                    return sortSettings.order === SortOrder.ASC ? 1 : -1;
                });
        }
    }, [flashcardAnalytics, membersById, sortSettings]);

    const renderTable = () => {
        return (
            <StyledTable
                rows={sortedRows}
                disableSelection
                hideOptionSection
                paginateBy={6}
                sortSettings={sortSettings}
                onSortChange={setSortSettings}
                cellProps={{
                    style: {
                        height: "8.4rem",
                        width: "14rem",
                    },
                }}
                containerStyle={{
                    border: `1px solid ${themeColors.background}`,
                }}
                tableContainerProps={{
                    sx: {
                        ".MuiTableCell-root": {
                            borderBottom: `1px solid ${themeColors.background}`,
                        },
                    },
                }}
                headerProps={{
                    style: {
                        backgroundColor: themeColors.neutralWhite,
                    },
                }}>
                <StyledTableColumn title="Name" sortKey={SortKeys.Name}>
                    {(row: string) => <StudentInformationCell member={membersById[row]} />}
                </StyledTableColumn>
                <StyledTableColumn title="Progress" sortKey={SortKeys.Progress}>
                    {(row: string) => (
                        <Circular
                            radius="auto"
                            style={{
                                width: "16rem",
                                height: "4rem",
                                color: getColor(flashcardAnalytics?.[row].progress || StudySessionProgressEnum.NEW),
                                backgroundColor: getBackgroundColor(
                                    flashcardAnalytics?.[row].progress || StudySessionProgressEnum.NEW
                                ),
                                gap: "0.8rem",
                                justifyContent: "flex-start",
                                padding: "0 0 0 1.5rem",
                                fontSize: "1.5rem",
                            }}>
                            <CircularProgress
                                value={getProgressValue(
                                    flashcardAnalytics?.[row].progress || StudySessionProgressEnum.NEW
                                )}
                                bgColor={getProgressBg(
                                    flashcardAnalytics?.[row].progress || StudySessionProgressEnum.NEW
                                )}
                                color={getColor(flashcardAnalytics?.[row].progress || StudySessionProgressEnum.NEW)}
                                size={"2rem"}
                                thickness={8}
                            />
                            {capitalize(flashcardAnalytics?.[row].progress || StudySessionProgressEnum.NEW)}
                        </Circular>
                    )}
                </StyledTableColumn>
                <StyledTableColumn title="Time studying" sortKey={SortKeys.TimeStudying}>
                    {(row: string) => (
                        <p
                            className="secondaryTextBold1"
                            data-testid="auto-id-2101c518-55d9-4023-83ff-23abc75c12f3">
                            {formatTimeStudying(flashcardAnalytics?.[row].timeSpent || 0)}
                        </p>
                    )}
                </StyledTableColumn>
            </StyledTable>
        );
    };

    return (
        <FlexColumn style={{ overflow: "auto", height: "100%", flex: 1, gap: spacing.LG }} className={"hide-scrollbar"}>
            <FlexColumn style={{ gap: spacing.XS }}>
                <span
                    className="heading5"
                    data-testid="auto-id-37d4c97d-3c95-4405-900d-8f5bcd6329b6">Student progress on flashcard</span>
                <span
                    className="secondaryTextBold1"
                    style={{
                        color: themeColors.neutral2,
                    }}
                    data-testid="auto-id-80800e1d-e598-41a3-b566-c9d1416c56f8">
                    View progress of each flashcard
                </span>
            </FlexColumn>
            <Flex
                style={{ gap: spacing.LG_2, height: "100%" }}
                className={clsx(br.mdDownColumnReverse, br.mdDownHeightAuto, "hide-scrollbar")}>
                {renderTable()}

                <FlexColumn
                    className={clsx(br.mdDownHeightAuto, br.mdDownWidth100)}
                    style={{
                        height: "100%",
                        justifyContent: "space-between",
                        width: "70%",
                    }}>
                    <FlexColumn
                        style={{
                            width: "100%",
                            gap: spacing.MD,
                            justifyContent: "space-between",
                            fontSize: "1.8rem",
                            flexDirection: "column",
                            border: `1px solid ${themeColors.neutral1}`,
                            padding: "2rem",
                            borderRadius: "2rem",
                            marginBottom: "2rem",
                        }}>
                        <Flex style={{ gap: spacing.SM, width: "35%" }}>
                            {secondaryImage && (
                                <AddRemoveImageButton
                                    editable={false}
                                    alt={term || "term image"}
                                    image={secondaryImage}
                                    size="9rem"
                                />
                            )}
                            <HtmlText text={term} />
                        </Flex>
                        <div
                            style={{ backgroundColor: themeColors.neutral1, height: "1px" }}
                            data-testid="auto-id-7bae75ed-b54f-4153-98ab-3c69406c70ad" />
                        <Flex style={{ gap: spacing.SM, width: "65%" }}>
                            {image && (
                                <AddRemoveImageButton
                                    editable={false}
                                    alt={definition ?? "definition image"}
                                    image={image}
                                    size="9rem"
                                />
                            )}
                            <HtmlText text={definition} />
                        </Flex>
                    </FlexColumn>

                    {/* NAVIGATION BUTTONS */}
                    <Flex style={{ justifyContent: "space-between", marginBottom: "2rem" }}>
                        <CircularRectTextButton
                            onClick={() => setFlashcardIndex(index => (index - 1 + flashcardsCount) % flashcardsCount)}
                            className="secondaryTextBold1"
                            sx={{
                                border: `1px solid ${themeColors.neutral1}`,
                                backgroundColor: "transparent",
                                padding: spacing.XS_2,
                                color: themeColors.neutralBlack,
                            }}>
                            <ArrowLeft
                                strokeWidth={strokeWidth.normal}
                                size={iconSizes.MD_S}
                                style={{ marginRight: "0.5rem" }}
                            />
                            Previous flashcard
                        </CircularRectTextButton>
                        <CircularRectTextButton
                            onClick={() => setFlashcardIndex(index => (index + 1 + flashcardsCount) % flashcardsCount)}
                            className="secondaryTextBold1"
                            sx={{
                                border: `1px solid ${themeColors.neutral1}`,
                                padding: spacing.XS_2,
                                backgroundColor: "transparent",
                                color: themeColors.neutralBlack,
                            }}>
                            Next flashcard
                            <ArrowRight
                                strokeWidth={strokeWidth.normal}
                                size={iconSizes.MD_S}
                                style={{ marginLeft: "0.5rem" }}
                            />
                        </CircularRectTextButton>
                    </Flex>
                </FlexColumn>
            </Flex>
        </FlexColumn>
    );
};

const MasteryFlashcardDetailsPopup = ({
    onClose,
    currentFlashcardIndex,
    ...contentProps
}: { onClose: () => void; currentFlashcardIndex?: number | null } & Omit<
    React.ComponentProps<typeof MasteryFlashcardDetailsContent>,
    "currentFlashcardIndex"
>) => {
    return (
        <ResponsiveDialog
            isOpen={currentFlashcardIndex !== null}
            onClose={onClose}
            style={{ backgroundColor: themeColors.neutralWhite, overflow: "hidden" }}
            DialogStyle={{ padding: "4rem", width: "120rem", height: "80vh", borderRadius: "4rem" }}
            SlideMenuStyle={{ padding: "2.5rem 2rem 1rem" }}>
            <DialogCloseBtn
                forDialogStyle={{ position: "absolute", top: "4rem", right: "4rem" }}
                forSlideMenuStyle={{ margin: "0 0 2rem auto" }}
                onClick={onClose}
            />
            {currentFlashcardIndex !== null && currentFlashcardIndex !== undefined && (
                <MasteryFlashcardDetailsContent {...contentProps} currentFlashcardIndex={currentFlashcardIndex} />
            )}
        </ResponsiveDialog>
    );
};

export default MasteryFlashcardDetailsPopup;
